import { NgIf } from '@angular/common';
import {
	ChangeDetectorRef,
	Component,
	ComponentRef,
	ElementRef,
	EventEmitter,
	Injector,
	Input,
	OnChanges,
	OnDestroy,
	Optional,
	Output,
	reflectComponentType,
	Type,
	ViewChild,
	ViewContainerRef
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
	PreviewSlide,
	ReportDateRange,
	SlideType
} from '../../../../models/preview/preview.models';
import {
	ReportContentItem,
	ReportDataSource
} from '../../../../services/report-settings.service';
import { ReportSharedReadsService } from '../../../../services/report-shared-reads.service';
import { ReportLanguageService } from '../../../../services/report-language.service';
import { reportMessage } from '../../../../services/report-messages';
import {
	numberSource,
	ReportSource,
	sectionSource
} from '../../../../services/report-source';
import {
	isThinMentionTotal,
	isThinSensitiveSection
} from '../../../../services/report-thin-data';
import {
	ReportBlockData,
	ReportBlockRender
} from '../../../../services/report-blocks.service';
import {
	ReportSectionChartSwitch,
	ReportSectionChartTypeChange
} from '../../../../services/report-chart-types';
import {
	isSourceSection,
	ReportSourceLayout,
	sourceSectionEntities,
	sourceSectionLayout,
	sourceSectionNote
} from '../../../../services/report-source-sections';
import { ReportSourceSectionsService } from '../../../../services/report-source-sections.service';
import { resolveSlide } from '../../registries/slide.registry';
import { SourceComparisonSlideComponent } from '../../slides/source-comparison-slide/source-comparison-slide.component';
import { ThinSectionSlideComponent } from '../../slides/thin-section-slide/thin-section-slide.component';
import { ReportSlideSourceComponent } from '../report-slide-source/report-slide-source.component';
import { sourceSlideProviders } from './report-source-slide-providers';

/**
 * Whether a volume-sensitive section may be drawn as itself: `pending` while
 * the report's mention count is read, `thin` when it is under the floor.
 */
type VolumeGate = 'open' | 'pending' | 'thin';

/**
 * The slide with its chart's numbers on it, for the figures its text quotes.
 *
 * CONTRACT: every chart slide works out its `chartSwitch` from the numbers it
 * fetched (`sectionChartSwitch`) BEFORE it emits slideBuilt — read here, in the
 * same turn, they are that build's numbers.
 */
function withChartNumbers(
	slide: PreviewSlide,
	instance: { chartSwitch?: ReportSectionChartSwitch | null }
): PreviewSlide {
	const numbers = instance.chartSwitch?.source ?? null;
	return numbers ? { ...slide, chartNumbers: numbers } : slide;
}

/** The source line's max-width in the stylesheet, as a share of the slide. */
const SOURCE_LINE_SHARE = 0.56;
/** The room kept between the source line and a footer cluster, in px. */
const SOURCE_LINE_GAP = 12;
const SOURCE_LINE_FITTED = 'slide-host__source--fitted';

const BRAND_READS = 'brand';

/**
 * Renders one fixed section, on every surface that shows a report — the
 * editor's cards, the PDF export, and anything else built from this host.
 *
 * Two things every section gets from here rather than from its own component,
 * so that sixteen slides do not each have to remember them: the source line
 * under its numbers, and the thin-data rule — a section that cannot be read
 * off a handful of mentions says so instead of drawing (report-thin-data.ts).
 *
 * A report WITH A SOURCE — a competitor project or an industry — has its
 * sections drawn about that source (T-2928), from the section route
 * (ReportSourceSectionsService), and never from the brand's analytics. The
 * answer decides the drawing (report-source-sections.ts): the section's own
 * slide, handed adapters in place of its analytics services, for one entity;
 * one slide comparing several (SourceComparisonSlideComponent); or the
 * section's frame with the answer's own note, which is also where the thin
 * rule lives for a source — each block comes back judged, per entity, so the
 * brand's mention count decides nothing here.
 */
@Component({
	standalone: true,
	selector: 'app-report-preview-slide-host',
	imports: [NgIf, ReportSlideSourceComponent],
	template: `
		<ng-container #host></ng-container>
		<div class="slide-host__source-layer" *ngIf="source">
			<app-report-slide-source
				#sourceLine
				class="slide-host__source"
				[source]="source"
			></app-report-slide-source>
		</div>
	`,
	styleUrls: ['./report-preview-slide-host.component.scss']
})
export class ReportPreviewSlideHostComponent implements OnChanges, OnDestroy {
	@Input() item!: ReportContentItem;
	@Input() brandId!: string;
	@Input() dateRange!: ReportDateRange;
	// The report's own colour. Every slide the registry can resolve declares
	// this input, because `setInput` throws on a component that does not.
	@Input() themeColor: string | null = null;
	@Input() editorMode = false;
	// CONTRACT: with both, and the page's ReportSourceSectionsService, the
	// section is drawn about the source. A page that passes them without
	// providing the service draws a source report's sections from the BRAND.
	@Input() dataSource: ReportDataSource | null = null;
	@Input() reportId: string | null = null;
	@Output() slideBuilt = new EventEmitter<PreviewSlide>();
	/** A section's chart was switched to another type on its slide. */
	@Output() chartTypeChange =
		new EventEmitter<ReportSectionChartTypeChange>();

	@ViewChild('host', { read: ViewContainerRef, static: true })
	host!: ViewContainerRef;

	@ViewChild('sourceLine', { read: ElementRef })
	private sourceLine?: ElementRef<HTMLElement>;

	/** Where this slide's numbers came from; null for one with none (the
	 *  cover). */
	source: ReportSource | null = null;

	private componentRef: ComponentRef<unknown> | null = null;
	private componentType: Type<unknown> | null = null;
	private slideType: SlideType | null = null;
	private componentReads: string | null = null;
	/**
	 * Whether the slide has editor-only UI to switch on. Unlike `themeColor`
	 * not every slide declares `editorMode` — only those with something to
	 * edit in place — so it is handed only to the ones that do.
	 */
	private acceptsEditorMode = false;
	// TRAP: handed only to a slide that declares `source` — setInput throws on
	// one that does not, and only the ones naming each figure's source do.
	private acceptsSource = false;
	private destroy$ = new Subject<void>();

	/** What the shared reads were last asked for, so a re-render with the
	 *  same inputs does not ask again. */
	private readsKey: string | null = null;
	private readsChanged$ = new Subject<void>();
	/** Set while the reads are subscribed to: a cached read answers at
	 *  once, and the render that follows picks the answer up itself. */
	private subscribing = false;
	/** Both: undefined while read, null when they could not be. */
	private brandLabel: string | null | undefined = undefined;
	private mentionTotal: number | null | undefined = undefined;
	// TRAP: undefined while it is read, null when it could not be — the one
	// is waited for, the other is drawn.
	private answer: ReportBlockData[] | null | undefined = undefined;
	/** A slide that finished before the shared reads answered. */
	private heldSlide: PreviewSlide | null = null;
	/** Watches the footer the source line has to keep clear of. */
	private footerWatch: ResizeObserver | null = null;
	private watchedFooter: Element | null = null;
	private watchedLine: HTMLElement | null = null;

	constructor(
		private sharedReads: ReportSharedReadsService,
		private cdr: ChangeDetectorRef,
		private element: ElementRef<HTMLElement>,
		private injector: Injector,
		@Optional() private reportLanguage: ReportLanguageService | null,
		@Optional()
		private sourceSections: ReportSourceSectionsService | null
	) {}

	ngOnChanges(): void {
		this.watchSharedReads();
		this.render();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.readsChanged$.next();
		this.readsChanged$.complete();
		this.componentRef?.destroy();
		this.host.clear();
		this.footerWatch?.disconnect();
	}

	private get aboutSource(): ReportDataSource | null {
		return this.dataSource &&
			this.reportId &&
			this.sourceSections &&
			this.item &&
			sectionSource(this.item.id, null, this.dateRange) !== null
			? this.dataSource
			: null;
	}

	private get gatesOnVolume(): boolean {
		return Boolean(
			this.item &&
				this.brandId &&
				this.dateRange &&
				!this.aboutSource &&
				isThinSensitiveSection(this.item.id)
		);
	}

	private get gate(): VolumeGate {
		if (!this.gatesOnVolume) return 'open';
		if (this.mentionTotal === undefined) return 'pending';
		return isThinMentionTotal(this.mentionTotal) ? 'thin' : 'open';
	}

	/**
	 * Whether a built slide may be announced: once the count has decided
	 * what this section is, and once the brand's name is known — the source
	 * line prints it, and in the PDF that line is text on the page, not a
	 * tooltip that can catch up later. A read that fails answers null and
	 * releases the slide all the same.
	 */
	private get settled(): boolean {
		if (this.aboutSource) {
			return this.answer !== undefined;
		}
		return this.brandLabel !== undefined && this.gate !== 'pending';
	}

	/**
	 * Ask for the brand's name and, for a volume-sensitive section, the
	 * report's mention count. Both are read once per page and shared by
	 * every slide (ReportSharedReadsService) — the count is the executive
	 * summary's own Mentions figure, from the same request.
	 */
	private watchSharedReads(): void {
		if (!this.item) return;
		const about = this.aboutSource;
		// Without a window there is nothing to read, and both answers below
		// come out as "none" rather than "still reading" — nothing is held.
		const key = about
			? [
					'source',
					this.reportId,
					about.kind,
					about.id,
					this.item.id,
					this.dateRange.start,
					this.dateRange.end
			  ].join('|')
			: [
					this.item.id,
					this.brandId,
					this.dateRange?.start,
					this.dateRange?.end
			  ].join('|');
		if (key === this.readsKey) return;
		this.readsKey = key;
		this.readsChanged$.next();
		this.answer = undefined;

		this.subscribing = true;
		if (about) {
			this.watchSourceSection();
		} else {
			this.watchBrandReads();
		}
		this.subscribing = false;
	}

	private watchBrandReads(): void {
		// Only a slide that prints a source line has a use for the name; the
		// cover has none and is not held for it.
		const namesBrand =
			Boolean(this.brandId) &&
			sectionSource(this.item.id, null, this.dateRange) !== null;
		this.brandLabel = namesBrand ? undefined : null;
		this.mentionTotal = undefined;

		if (namesBrand) {
			this.sharedReads
				.brandLabel(this.brandId)
				.pipe(takeUntil(this.readsChanged$))
				.subscribe(label => {
					this.brandLabel = label;
					this.onSharedRead();
				});
		}
		if (this.gatesOnVolume) {
			this.sharedReads
				.mentionTotal(this.brandId, this.dateRange)
				.pipe(takeUntil(this.readsChanged$))
				.subscribe(total => {
					this.mentionTotal = total;
					this.onSharedRead();
				});
		}
	}

	// CONTRACT: the same read the adapters of the section's own slide are
	// answered from — the page's service keeps one per section and window.
	private watchSourceSection(): void {
		this.brandLabel = null;
		this.mentionTotal = undefined;
		if (!isSourceSection(this.item.id)) {
			this.answer = [];
			return;
		}
		this.sourceSections!.section(
			this.reportId!,
			this.item.id,
			this.dateRange.start,
			this.dateRange.end
		)
			.pipe(takeUntil(this.readsChanged$))
			.subscribe(answer => {
				this.answer = answer;
				this.onSharedRead();
			});
	}

	private onSharedRead(): void {
		if (this.subscribing) return;
		this.render();
		// The export page detaches its change detector once its view is
		// built, so nothing redraws this host's own template — the source
		// line — unless the host asks.
		this.cdr.detectChanges();
		this.watchFooterSoon();
	}

	private render(): void {
		if (!this.host || !this.item) return;
		const slideType = this.item.id as SlideType;
		const about = this.aboutSource;
		const language = this.reportLanguage?.language;
		const reads = about
			? `${this.reportId}|${about.kind}|${about.id}`
			: BRAND_READS;
		const gate: VolumeGate = about ? 'open' : this.gate;
		const layout = about ? this.sourceLayout() : null;
		const componentType = about
			? this.chooseSourceComponent(slideType, layout!, reads)
			: this.chooseComponent(slideType, gate);

		this.source = about
			? this.sourceSectionLine(about, language)
			: this.brandSectionLine(gate, language);

		if (
			!this.componentRef ||
			this.componentType !== componentType ||
			this.slideType !== slideType ||
			this.componentReads !== reads
		) {
			this.destroy$.next();
			this.heldSlide = null;
			this.host.clear();
			this.componentRef = this.host.createComponent(
				componentType,
				about ? { injector: this.sourceInjector() } : undefined
			);
			this.componentType = componentType;
			this.slideType = slideType;
			this.componentReads = reads;
			const inputs = reflectComponentType(componentType)?.inputs ?? [];
			this.acceptsEditorMode = inputs.some(
				input => input.templateName === 'editorMode'
			);
			this.acceptsSource = inputs.some(
				input => input.templateName === 'source'
			);
			this.subscribeToSlideBuilt();
			this.subscribeToChartTypeChange();
		}

		this.componentRef.setInput('item', this.item);
		// TRAP: every slide waits for a brandId before it reads, and a report
		// homed on a project or an industry has none. The source stands in —
		// in the slide's widget ids too, which is where its saved edits live.
		this.componentRef.setInput(
			'brandId',
			about ? this.brandId || about.id : this.brandId
		);
		this.componentRef.setInput('dateRange', this.dateRange);
		this.componentRef.setInput('themeColor', this.themeColor);
		if (componentType === ThinSectionSlideComponent) {
			this.componentRef.setInput('mentionTotal', this.mentionTotal ?? 0);
			// TRAP: kept while a new answer is read, as a drawn chart is —
			// set now, it would flash the empty message until the answer lands.
			if (!about || layout !== 'pending') {
				this.componentRef.setInput(
					'note',
					about ? this.sourceNote(language) : null
				);
			}
		}
		if (
			componentType === SourceComparisonSlideComponent &&
			this.answer !== undefined
		) {
			this.componentRef.setInput('blocks', this.answer ?? []);
		}
		if (this.acceptsSource) {
			this.componentRef.setInput('source', about ? this.source : null);
		}
		if (this.acceptsEditorMode) {
			this.componentRef.setInput('editorMode', this.editorMode);
		}
		this.componentRef.changeDetectorRef.detectChanges();
		this.watchFooterSoon();

		if (this.settled && this.heldSlide) {
			const slide = this.heldSlide;
			this.heldSlide = null;
			this.announce(slide);
		}
	}

	// TRAP: a thin section shows one number, the mention count, so that is
	// what its source line accounts for — not the chart that is not there.
	private brandSectionLine(
		gate: VolumeGate,
		language: string | null | undefined
	): ReportSource | null {
		const source = sectionSource(
			this.item.id,
			this.brandLabel ?? null,
			this.dateRange,
			language
		);
		return source && gate === 'thin'
			? numberSource(
					source,
					reportMessage(language, 'source.measure.mentions')
			  )
			: source;
	}

	private sourceSectionLine(
		about: ReportDataSource,
		language: string | null | undefined
	): ReportSource | null {
		if (!isSourceSection(this.item.id)) {
			return null;
		}
		return sectionSource(this.item.id, null, this.dateRange, language, {
			dataSource: about,
			entities: sourceSectionEntities(this.answer ?? [])
		});
	}

	private sourceNote(language: string | null | undefined): string {
		if (!isSourceSection(this.item.id)) {
			return reportMessage(language, 'source.section.brand_only');
		}
		return (
			sourceSectionNote(this.answer ?? []) ??
			reportMessage(language, 'empty.nothing')
		);
	}

	private sourceLayout(): ReportSourceLayout | 'pending' {
		return this.answer === undefined
			? 'pending'
			: sourceSectionLayout(this.item.id, this.answer);
	}

	/**
	 * The section's own slide, or the thin state in its place.
	 *
	 * The real slide is created while the count is still being read rather
	 * than after it: its own fetches start at once, so a report with plenty
	 * of mentions — nearly all of them — pays nothing for the check. Only
	 * its announcement waits (see subscribeToSlideBuilt). And while the
	 * count is undecided whatever is already drawn stays, so a re-read
	 * never swaps a slide out and back.
	 */
	private chooseComponent(
		slideType: SlideType,
		gate: VolumeGate
	): Type<unknown> {
		if (gate === 'thin') {
			return ThinSectionSlideComponent;
		}
		if (
			gate === 'pending' &&
			this.componentType &&
			this.slideType === slideType &&
			this.componentReads === BRAND_READS
		) {
			return this.componentType;
		}
		return resolveSlide(slideType);
	}

	// TRAP: until the answer is in, whatever is drawn stays, and a first draw
	// is the section's own slide — its reads wait on the same answer, so for
	// one entity, an industry most often, nothing is drawn twice.
	private chooseSourceComponent(
		slideType: SlideType,
		layout: ReportSourceLayout | 'pending',
		reads: string
	): Type<unknown> {
		switch (layout) {
			case 'note':
				return ThinSectionSlideComponent;
			case 'compare':
				return SourceComparisonSlideComponent;
			case 'own':
				return resolveSlide(slideType);
			default:
				return this.componentType &&
					this.slideType === slideType &&
					this.componentReads === reads
					? this.componentType
					: resolveSlide(slideType);
		}
	}

	// TRAP: the slide asks for its analytics service BY TYPE, and this
	// injector answers with an adapter over the section route
	// (report-source-slide-providers.ts). A slide that provided that service
	// itself, or read it from anywhere but its own constructor, would draw
	// the BRAND's numbers under the source's name.
	private sourceInjector(): Injector {
		return Injector.create({
			providers: sourceSlideProviders(
				{
					reportId: this.reportId!,
					sectionId: this.item.id,
					sections: this.sourceSections!
				},
				this.injector
			),
			parent: this.injector
		});
	}

	private subscribeToSlideBuilt(): void {
		const instance = this.componentRef?.instance as any;
		if (instance?.slideBuilt) {
			instance.slideBuilt
				.pipe(takeUntil(this.destroy$))
				.subscribe((built: PreviewSlide) => {
					const slide = withChartNumbers(built, instance);
					this.componentRef?.changeDetectorRef.detectChanges();
					// Not announced until the shared reads have answered (see
					// `settled`). The editor's loader and the PDF's readiness
					// both wait on this event, so holding it is what keeps
					// either from revealing a chart that is about to be
					// replaced by the thin state, or printing a source line
					// that has not been told whose numbers these are.
					if (!this.settled) {
						this.heldSlide = slide;
						return;
					}
					// TRAP: a newer build supersedes a held one — left held,
					// render() would announce the older slide after this one.
					this.heldSlide = null;
					this.announce(slide);
				});
		}
	}

	private announce(slide: PreviewSlide): void {
		requestAnimationFrame(() => {
			this.componentRef?.changeDetectorRef.detectChanges();
			this.watchFooter();
			this.slideBuilt.emit(slide);
		});
	}

	/** Passed up with the section it came from; the slide only knows itself. */
	private subscribeToChartTypeChange(): void {
		const instance = this.componentRef?.instance as any;
		if (instance?.chartTypeChange) {
			instance.chartTypeChange
				.pipe(takeUntil(this.destroy$))
				.subscribe((chartType: ReportBlockRender | null) =>
					this.chartTypeChange.emit({
						sectionId: this.item.id,
						chartType
					})
				);
		}
	}

	/** After the DOM a render or a read changed has been drawn. */
	private watchFooterSoon(): void {
		requestAnimationFrame(() => this.watchFooter());
	}

	/**
	 * Keeps the source line clear of the footer's two clusters — "Powered by
	 * lolly", with an agency's logo and name when it has a brand kit, and the
	 * section tag. Centred on the slide it sits between them on a wide slide;
	 * on a narrow one, or beside a wide kit, it takes the gap between them
	 * instead and ellipsises there. Watched, because the kit's logo loads
	 * after the slide is drawn and the slide changes width with its column.
	 */
	private watchFooter(): void {
		const footer =
			this.element.nativeElement.querySelector('.slide__footer');
		const line = this.sourceLine?.nativeElement ?? null;
		if (footer === this.watchedFooter && line === this.watchedLine) {
			this.fitSourceLine();
			return;
		}
		this.footerWatch?.disconnect();
		this.footerWatch = null;
		this.watchedFooter = footer;
		this.watchedLine = line;
		if (!footer || !line || typeof ResizeObserver === 'undefined') {
			line?.classList.remove(SOURCE_LINE_FITTED);
			return;
		}
		this.footerWatch = new ResizeObserver(() => this.fitSourceLine());
		this.footerWatch.observe(this.element.nativeElement);
		Array.from(footer.children).forEach(child =>
			this.footerWatch?.observe(child)
		);
		this.fitSourceLine();
	}

	// TRAP: measured in the slide's own layout pixels. The editor and present
	// mode scale a slide with a transform, and a client rect is in scaled ones.
	private fitSourceLine(): void {
		const host = this.element.nativeElement;
		const line = this.watchedLine;
		const footer = this.watchedFooter;
		const width = host.offsetWidth;
		if (!line || !footer || !width) return;
		const box = host.getBoundingClientRect();
		const scale = box.width / width || 1;
		const toSlide = (x: number): number => (x - box.left) / scale;
		const clusters = Array.from(footer.children).filter(
			child => child.getBoundingClientRect().width > 0
		);
		const first = clusters[0];
		const last = clusters.length > 1 ? clusters[clusters.length - 1] : null;
		const left = first
			? toSlide(first.getBoundingClientRect().right) + SOURCE_LINE_GAP
			: 0;
		const right = last
			? toSlide(last.getBoundingClientRect().left) - SOURCE_LINE_GAP
			: width;
		const text = line.querySelector<HTMLElement>('.report-source');
		const shown = Math.min(
			text?.scrollWidth ?? 0,
			width * SOURCE_LINE_SHARE
		);
		const collides =
			width / 2 - shown / 2 < left || width / 2 + shown / 2 > right;
		line.classList.toggle(SOURCE_LINE_FITTED, collides);
		if (collides) {
			line.style.setProperty(
				'--slide-source-left',
				`${Math.round(left)}px`
			);
			line.style.setProperty(
				'--slide-source-right',
				`${Math.round(width - right)}px`
			);
		}
	}
}
